// eslint-disable-next-line import/no-extraneous-dependencies
import { Plugin } from '@nuxt/types';
import storeMutations from '~/store/constants/storeMutations';
import { setCookie, getCookie } from '~/utils/cookies';

const GEOLOCATION_STORAGE_KEY = 'fc_visitor_country';
const GEOLOCATION_TIMESTAMP_KEY = 'fc_visitor_country_timestamp';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
const API_TIMEOUT = 1200; // 1.2 seconds
const SUPPORTED_COUNTRIES = ['US', 'CA'] as const;
const DEFAULT_COUNTRY = 'US';

interface GeoResponse {
  countryCode: string;
}

const geolocationPlugin: Plugin = async ({ store, route, app }, _inject) => {
  if (process.server) return;

  // Step 1: Market override (Shopping ads/local development) wins
  const countryFromMarket = getCountryOverride(route);

  // Step 2: Country detection (cache -> api)
  const country = (countryFromMarket || await detectCountry(app.$bugsnag)).toUpperCase();

  // Step 3: Store country in Vuex
  store.commit(storeMutations.SET_GEOLOCATION_COUNTRY, country);

  // Step 4: Fetch products with correct country on initial load
  // Pass country explicitly to avoid reactive dependency on state.geoLocationCountry
  await store.dispatch('getCollection', {
    id: process.env.NUXT_ENV_SHOPIFY_COLLECTION_ID,
    country,
  });
};

/**
 * Checks for country override from query param or cookie (for testing)
 */
function getCountryOverride(route: any): string | null {
  // Check query parameter first (e.g., ?market=CA or ?market=US)
  const raw = (route?.query?.market as string | undefined) || null;
  const market = raw ? raw.toUpperCase() : null;

  if (market && SUPPORTED_COUNTRIES.includes(market as any)) {
    const country = market;
    setCookie('fc_country_override', country, 1);
    return country;
  }

  // Check cookie override (persists from previous query param)
  const storedOverride = getCookie('fc_country_override');
  if (storedOverride) {
    const normalized = storedOverride.toUpperCase();
    if (SUPPORTED_COUNTRIES.includes(normalized as any)) {
      return normalized;
    }
  }

  return null;
}

/**
 * Detects country from cache or API
 */
async function detectCountry(bugsnag?: any): Promise<string> {
  // Check cache first
  const cachedCountry = getCookie(GEOLOCATION_STORAGE_KEY);
  const cachedTimestamp = getCookie(GEOLOCATION_TIMESTAMP_KEY);

  if (cachedCountry && cachedTimestamp) {
    const timestamp = parseInt(cachedTimestamp, 10);
    const isValid = Number.isFinite(timestamp) && Date.now() - timestamp < CACHE_DURATION;

    if (isValid) {
      const normalized = cachedCountry.toUpperCase();
      return validateCountry(normalized);
    }
  }

  // Make API call to Netlify function
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT);

  try {
    const response = await fetch('/.netlify/functions/geo', {
      signal: controller.signal,
    });

    if (!response.ok) {
      throw new Error(`Geo API failed: ${response.status}`);
    }

    const data: GeoResponse = await response.json();
    const rawCountry = (data.countryCode || DEFAULT_COUNTRY).toUpperCase();
    const country = validateCountry(rawCountry);

    // Cache the result
    setCookie(GEOLOCATION_STORAGE_KEY, country, 1);
    setCookie(GEOLOCATION_TIMESTAMP_KEY, Date.now().toString(), 1);

    return country;
  } catch (error) {
    console.error('Failed to fetch geolocation:', error);

    // Report error to Bugsnag (skip expected network failures)
    const isAbortError = error instanceof Error && (
      error.name === 'AbortError'
      || error.message.includes('aborted')
      || error.message.includes('Fetch is aborted')
    );
    const isNetworkError = error instanceof TypeError && error.message === 'Load failed';

    if (bugsnag && !isAbortError && !isNetworkError) {
      const errorToReport = error instanceof Error ? error : new Error(String(error));
      errorToReport.message = `Geolocation API error: ${errorToReport.message}`;
      bugsnag.notify(errorToReport, 'warning', 'geolocation:detectCountry');
    }

    // Prefer stale cached value without extending TTL
    if (cachedCountry) {
      return validateCountry(cachedCountry.toUpperCase());
    }

    // Only write default if we truly have nothing
    const fallback = DEFAULT_COUNTRY;
    setCookie(GEOLOCATION_STORAGE_KEY, fallback, 1);
    setCookie(GEOLOCATION_TIMESTAMP_KEY, Date.now().toString(), 1);
    return fallback;
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Validates country code against supported countries
 * Returns the country if supported, otherwise returns default
 */
function validateCountry(country: string): string {
  return SUPPORTED_COUNTRIES.includes(country as any) ? country : DEFAULT_COUNTRY;
}

export default geolocationPlugin;
